import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Youtube } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SectionHead, EASE } from "./SectionHead";
import { asset } from "@/lib/api";
import { youtubeId } from "@/hooks/useList";
import { railTrack, railItem, SwipeHint } from "./Rail";

const Thumb = ({ v }) => {
  const ref = useRef(null);
  const yt = v.source === "youtube" ? youtubeId(v.url) : null;
  const poster = v.thumbnail_url ? asset(v.thumbnail_url) : yt ? `https://img.youtube.com/vi/${yt}/hqdefault.jpg` : "";
  const hover = (on) => {
    if (!ref.current) return;
    if (on) ref.current.play().catch(() => {});
    else ref.current.pause();
  };
  return (
    <div className="absolute inset-0" onMouseEnter={() => hover(true)} onMouseLeave={() => hover(false)}>
      {v.source === "upload" ? (
        <video ref={ref} src={`${asset(v.url)}#t=0.5`} poster={poster || undefined} muted loop playsInline preload="metadata" className="w-full h-full object-cover" />
      ) : (
        poster && <img src={poster} alt={v.title} loading="lazy" className="w-full h-full object-cover" />
      )}
    </div>
  );
};

const Card = ({ v, i, big, onPlay }) => (
  <motion.button data-testid={`timelapse-card-${v.id}`} onClick={() => onPlay(v)}
    initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.9, delay: (i % 3) * 0.08, ease: EASE }}
    className={`group text-left clip-corner bg-[#F5A524]/25 hover:bg-[#F5A524] p-px transition-colors duration-300 ${railItem} ${big ? "md:col-span-2 md:row-span-2" : ""}`}>
    <div className="clip-corner bg-[#12131A] h-full flex flex-col">
      <div className="relative aspect-video overflow-hidden flex-1">
        <Thumb v={v} />
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/0 transition-colors" />
        <span className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center bg-[#F5A524] text-black rounded-full transition-transform duration-300 group-hover:scale-110 ${big ? "w-20 h-20" : "w-14 h-14"}`}>
          <Play size={big ? 30 : 22} className="fill-current ml-1" />
        </span>
        <span className="absolute top-3 left-3 bg-black/75 px-2 py-1 font-mono text-[11px] tracking-[0.2em] text-[#F5A524] flex items-center gap-1">
          {v.source === "youtube" && <Youtube size={12} />} TIMELAPSE{v.duration ? ` // ${v.duration}` : ""}
        </span>
      </div>
      <div className="p-4 sm:p-5">
        <h3 className={`font-display font-black uppercase leading-none group-hover:text-[#F5A524] transition-colors ${big ? "text-4xl" : "text-2xl"}`}>{v.title}</h3>
        {v.description && <p className="mt-2 text-sm text-zinc-400 line-clamp-2">{v.description}</p>}
      </div>
    </div>
  </motion.button>
);

const Player = ({ v }) => {
  const yt = v.source === "youtube" ? youtubeId(v.url) : null;
  if (yt) return <iframe data-testid="timelapse-youtube-player" title={v.title} src={`https://www.youtube-nocookie.com/embed/${yt}?autoplay=1&rel=0`} allow="autoplay; encrypted-media; fullscreen" allowFullScreen className="w-full aspect-video border-0" />;
  return <video data-testid="timelapse-video-player" src={asset(v.url)} controls autoPlay playsInline className="w-full max-h-[80vh] bg-black" />;
};

export const Timelapses = ({ index, videos }) => {
  const [playing, setPlaying] = useState(null);
  return (
    <section id="timelapse" data-testid="timelapse-section" className="relative px-5 sm:px-10 lg:px-16 py-16 md:py-24 border-t border-[#F5A524]/15">
      <SectionHead index={index} kicker="TIMELAPSE" title={<>Hours in<br /><span className="text-stroke">Minutes</span></>}>
        <p className="max-w-sm text-base text-zinc-300 leading-relaxed">Watch pieces come together, from first line to final color.</p>
      </SectionHead>
      <SwipeHint count={videos.length} testid="timelapse-swipe-hint" />
      <div className={`${railTrack} md:grid md:grid-cols-3 md:gap-5 md:items-stretch`}>
        {videos.map((v, i) => <Card key={v.id} v={v} i={i} big={i === 0 && videos.length > 2} onPlay={setPlaying} />)}
      </div>
      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent data-testid="timelapse-dialog" className="bg-[#0B0C10] border-[#F5A524]/40 rounded-none max-w-5xl p-0 gap-0 overflow-hidden">
          <DialogTitle className="px-5 py-3 font-mono text-xs tracking-[0.25em] text-[#F5A524] border-b border-[#F5A524]/20 font-normal">▶ {playing?.title}</DialogTitle>
          {playing && <Player v={playing} />}
        </DialogContent>
      </Dialog>
    </section>
  );
};
